import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || 'https://api.ardacious.app';

const SECTOR_OPTIONS = ['Energy','LNG','Oil & Gas','Power','Carbon','Mining','Soft Commodities','Metals','Banking','Capital Markets','Sovereign Capital','Funds','Tech','Infrastructure','Government','Geopolitics','Other'];
const ROLE_OPTIONS = [
  { v: 'buyer', l: 'Buyer' }, { v: 'seller', l: 'Seller' }, { v: 'financer', l: 'Financer' },
  { v: 'logistics', l: 'Logistics' }, { v: 'licensee', l: 'Licensee' }, { v: 'sponsor', l: 'Sponsor' },
  { v: 'intro', l: 'Introduction' }, { v: 'other', l: 'Other' },
];
const TICKET_RANGES = ['Under $1M','$1M - $5M','$5M - $20M','$20M - $100M','$100M - $500M','$500M+'];
const GEOGRAPHY_OPTIONS = ['Global','North America','South America','Europe','Middle East','Africa','Asia Pacific','Atlantic Basin','Pacific Basin','Caribbean','Other'];

export function SubmitIntake() {
  const { groupSlug } = useParams<{ groupSlug: string }>();
  const [groupName, setGroupName] = useState('Inner Circle');
  const [status, setStatus] = useState<'idle'|'loading'|'success'|'error'>('idle');
  const [referenceId, setReferenceId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [rateLimited, setRateLimited] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', sector: '', geography: '', productGrade: '',
    ticketSizeRange: '', rolesNeeded: [] as string[],
    submitterName: '', submitterEmail: '', submitterCompany: '', submitterPhone: '',
  });

  useEffect(() => {
    if (!groupSlug) return;
    fetch(`${API}/intake/${groupSlug}`).then(r => r.ok ? r.json() : null).then(d => { if (d?.groupName) setGroupName(d.groupName); }).catch(() => {});
  }, [groupSlug]);

  function set(field: string, value: string) { setForm(f => ({ ...f, [field]: value })); }
  function toggleRole(r: string) {
    setForm(f => ({ ...f, rolesNeeded: f.rolesNeeded.includes(r) ? f.rolesNeeded.filter(x => x !== r) : [...f.rolesNeeded, r] }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!groupSlug) return;
    setStatus('loading'); setErrorMsg('');
    try {
      const res = await fetch(`${API}/intake/${groupSlug}`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.status === 429) { setRateLimited(true); setStatus('error'); return; }
      if (!res.ok) { setErrorMsg(typeof data.error === 'string' ? data.error : 'Submission failed.'); setStatus('error'); return; }
      setReferenceId(data.referenceId); setStatus('success');
    } catch { setErrorMsg('Network error. Please try again.'); setStatus('error'); }
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center py-16">
          <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-3">Submission received</h1>
          <p className="text-gray-500 mb-6 text-sm leading-relaxed">Thank you. Your opportunity has been submitted to <strong>{groupName}</strong> for review. A member will be in touch if there is interest.</p>
          <div className="bg-gray-50 rounded-lg px-4 py-3 text-left mb-6">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">Reference ID</p>
            <p className="font-mono text-sm text-gray-700 break-all">{referenceId}</p>
          </div>
        </div>
      </div>
    );
  }

  const inputCls = "w-full px-3 py-2 text-base border border-gray-200 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900";
  const labelCls = "block text-xs font-medium text-gray-700 mb-1";

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-100 px-6 py-4">
        <div className="max-w-xl mx-auto flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#111" strokeWidth="1.5"/><circle cx="10" cy="10" r="3" fill="#111"/></svg>
          <span className="text-sm font-semibold tracking-tight text-gray-900">{groupName}</span>
          <span className="text-xs text-gray-400 ml-1">deal intake</span>
        </div>
      </header>
      <div className="max-w-xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-2">Submit an opportunity</h1>
          <p className="text-sm text-gray-500 leading-relaxed">Share a deal or opportunity with {groupName}. If there is interest, a member will reach out directly. All submissions are reviewed confidentially.</p>
        </div>
        <form onSubmit={submit} className="space-y-6">
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Opportunity details</h2>
            <div className="space-y-4">
              <div><label className={labelCls}>Title <span className="text-red-400">*</span></label><input className={inputCls} placeholder="e.g. West African crude cargo, $45M ticket" value={form.title} onChange={e => set('title', e.target.value)} required /></div>
              <div><label className={labelCls}>Description</label><textarea className={inputCls + ' resize-none'} rows={4} placeholder="Describe the opportunity, timeline, key terms..." value={form.description} onChange={e => set('description', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Sector</label><select className={inputCls} value={form.sector} onChange={e => set('sector', e.target.value)}><option value="">Select sector</option>{SECTOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className={labelCls}>Geography</label><select className={inputCls} value={form.geography} onChange={e => set('geography', e.target.value)}><option value="">Select region</option>{GEOGRAPHY_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Product / grade</label><input className={inputCls} placeholder="e.g. Bonny Light, LNG DES" value={form.productGrade} onChange={e => set('productGrade', e.target.value)} /></div>
                <div><label className={labelCls}>Ticket size range</label><select className={inputCls} value={form.ticketSizeRange} onChange={e => set('ticketSizeRange', e.target.value)}><option value="">Select range</option>{TICKET_RANGES.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
              </div>
              <div>
                <label className={labelCls}>Roles needed</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ROLE_OPTIONS.map(r => (
                    <button key={r.v} type="button" onClick={() => toggleRole(r.v)} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.rolesNeeded.includes(r.v) ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>{r.l}</button>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Your details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Name <span className="text-red-400">*</span></label><input className={inputCls} placeholder="Your full name" value={form.submitterName} onChange={e => set('submitterName', e.target.value)} required /></div>
                <div><label className={labelCls}>Email <span className="text-red-400">*</span></label><input type="email" className={inputCls} placeholder="you@company.com" value={form.submitterEmail} onChange={e => set('submitterEmail', e.target.value)} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Company</label><input className={inputCls} placeholder="Your company" value={form.submitterCompany} onChange={e => set('submitterCompany', e.target.value)} /></div>
                <div><label className={labelCls}>Phone</label><input type="tel" className={inputCls} placeholder="+1 555 000 0000" value={form.submitterPhone} onChange={e => set('submitterPhone', e.target.value)} /></div>
              </div>
            </div>
          </section>
          {errorMsg && <div className="rounded-md bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">{errorMsg}</div>}
          {rateLimited && <div className="rounded-md bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-700">Too many submissions from this connection. Please wait an hour before trying again.</div>}
          <div>
            <button type="submit" disabled={status === 'loading'} className="w-full bg-gray-900 text-white text-sm font-medium py-2.5 rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{status === 'loading' ? 'Submitting...' : 'Submit opportunity'}</button>
            <p className="mt-3 text-xs text-gray-400 text-center">By submitting you agree that this information may be shared confidentially with group members.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
